import ApiService from '@/services/ApiService';
import { TokenService } from '../TokenService';
import {
  showErrorSnackbar,
  createErrorMessage,
  showSnackbar
} from '../../util/snackBarUtil';
import {
  UserLoginType,
  AuthenticationError,
  SignupData,
  ConfirmationError,
  AuthenticationErrorCode,
  LoginResponse,
  OtpLoginData,
  OtpInputData
} from '@/store/types/auth';
import {
  authenticationStore,
  LOCAL_AUTH_STORE
} from '@/store/pinia/AuthenticationStore';
import router from '@/router';
import { i18n } from '@/plugins/i18n';
import { InvitationInformation } from '@/store/types/identity';
import {
  clearSwitchedAccount,
  clearUserManagerAndSession,
  getUserManager,
  oAuthEnabled
} from '../OidcService';
import { setLoginWithPasswordInLocalStorage } from '@/util/authentication';

export const configToAuthFrontend = {
  auth: {
    username: (import.meta.env.VITE_CLIENT_ID as string) || '',
    password: (import.meta.env.VITE_CLIENT_SECRET as string) || ''
  }
};

const AuthApi = {
  loginWithOtp: async (
    otp: OtpInputData,
    token: string,
    challenge: string
  ): Promise<LoginResponse> => {
    authenticationStore().resetErrors();
    const totpUrl = import.meta.env.VITE_API_TOTP_LOGIN_URL as string;
    const secrets = {
      token: token,
      challenge: challenge
    };
    const requestData: OtpLoginData = {
      ...secrets,
      ...(otp.withRecoveryCode ? { recoveryCode: otp.code } : { otp: otp.code })
    };
    try {
      const response = await ApiService.post(
        '',
        requestData,
        configToAuthFrontend,
        totpUrl
      );
      const loginResponse = response.data as LoginResponse;
      return loginResponse;
    } catch (error) {
      const e = new AuthenticationError(
        error.response.status,
        error.response.data.message,
        error.response.data.code,
        error.response.data.data
      );
      if (
        e.code !== AuthenticationErrorCode.WRONG_RECOVERY_CODE &&
        e.code !== AuthenticationErrorCode.WRONG_OTP
      ) {
        authenticationStore().setError(e);
      }
      throw e;
    }
  },
  login: async (
    user: UserLoginType,
    challenge?: string
  ): Promise<LoginResponse> => {
    const loginURL = challenge
      ? (import.meta.env.VITE_API_OIDC_LOGIN_URL as string)
      : (import.meta.env.VITE_API_LOGIN_URL as string);

    const requestData = {
      username: user.username,
      password: user.password,
      challenge: challenge
    };
    if (!challenge) delete requestData.challenge;
    try {
      const response = await ApiService.post(
        '',
        requestData,
        configToAuthFrontend,
        loginURL
      );
      const loginResponse = response.data as LoginResponse;
      if (!oAuthEnabled()) {
        TokenService.saveAccessToken(loginResponse.access ?? '');
        TokenService.saveRefreshToken(loginResponse.refresh ?? '');
      }
      clearSwitchedAccount();
      setLoginWithPasswordInLocalStorage(true);
      return loginResponse;
    } catch (error) {
      throw new AuthenticationError(
        error.response.status,
        error.response.data.message,
        error.response.data.code,
        error.response.data.data
      );
    }
  },
  signup: async (signupData: SignupData): Promise<string> => {
    const signUpURL = import.meta.env.VITE_API_SIGNUP_URL as string;
    const response = await ApiService.post(
      '',
      signupData,
      configToAuthFrontend,
      signUpURL
    );
    TokenService.saveAccessToken(response.data.access);
    TokenService.saveRefreshToken(response.data.refresh);
    return response.data.access;
  },

  signupWithInvitationCode: async (
    invitationCode: string,
    password: string
  ): Promise<void> => {
    const signUpURL = import.meta.env.VITE_API_SIGNUP_URL as string;
    const emptyAuthHeader = {
      headers: {
        Authorization: ''
      }
    };
    const signupData = {
      password: password
    };
    await ApiService.put(
      '',
      signupData,
      emptyAuthHeader,
      `${signUpURL}/invitation/${invitationCode}`
    );
  },
  requestInvitationInformation: async (
    invitationCode: string
  ): Promise<InvitationInformation> => {
    const signUpURL = import.meta.env.VITE_API_SIGNUP_URL as string;
    const response = await ApiService.get<InvitationInformation>(
      '',
      configToAuthFrontend,
      `${signUpURL}/invitation/${invitationCode}`
    );
    return response.data;
  },

  // Not really used! Only used in QuickstartWizard, not testable,
  checkPassword: async (password: string) => {
    const checkPasswordURL = import.meta.env
      .VITE_API_CHECK_PASSWORD_URL as string;
    const passwordDto = { password: password ?? '' };
    let response = { data: { passwordConfirmed: false } };
    if (!password) {
      return response.data;
    }
    try {
      // auth?
      response = await ApiService.post('', passwordDto, {}, checkPasswordURL);
    } catch (error) {
      showErrorSnackbar(createErrorMessage(error));
    }
    return response.data;
  },

  logout: async () => {
    const logoutURL = import.meta.env.VITE_API_LOGOUT_URL as string;
    if (oAuthEnabled()) {
      const manager = await getUserManager();
      await manager?.signoutRedirect();
      await clearUserManagerAndSession();
    } else {
      const refreshTokenValue = await TokenService.getRefreshToken();
      const accessTokenValue = await TokenService.getAccessToken();
      const requestData = {
        access: accessTokenValue,
        refresh: refreshTokenValue
      };

      try {
        await ApiService.post('', requestData, configToAuthFrontend, logoutURL);
        AuthApi.cleanUpAuthData();
        await router.push('/login');
        clearSwitchedAccount();
      } catch (error) {
        await AuthApi.cleanUpAndRedirectToLogin();
        throw new AuthenticationError(
          error.response.status,
          error.response.data.message,
          AuthenticationErrorCode.ERROR
        );
      }
    }
  },

  confirm: async (confirmationCode: string) => {
    const confirmURL = `${import.meta.env.VITE_AUTH_URL}/signup/confirm/${confirmationCode}`;
    try {
      await ApiService.put('', {}, configToAuthFrontend, confirmURL);
    } catch (error) {
      throw new ConfirmationError(error.status, error.data.message);
    }
  },

  sendResetPasswordLink: async (username: string) => {
    const requestResetPasswordURL = `${import.meta.env.VITE_AUTH_URL as string}/signup/reset-password/${username}`;
    try {
      await ApiService.post(
        '',
        {},
        configToAuthFrontend,
        requestResetPasswordURL
      );
    } catch {
      //
    }
  },

  resetPassword: async (resetPasswordCode: string, password: string) => {
    const resetPasswordURL = `${import.meta.env.VITE_AUTH_URL as string}/signup/reset-password/${resetPasswordCode}`;
    const requestData = {
      password: password
    };

    try {
      await ApiService.put(
        '',
        requestData,
        configToAuthFrontend,
        resetPasswordURL
      );
    } catch (error) {
      throw new AuthenticationError(
        error.status,
        error.data.message,
        AuthenticationErrorCode.ERROR
      );
    }
  },
  checkIfAuthStateIsValid: async () => {
    const authLocalStorageState = localStorage.getItem(LOCAL_AUTH_STORE);
    const refreshToken = await TokenService.getRefreshToken();
    if (!refreshToken || !authLocalStorageState) {
      AuthApi.cleanUpAuthData();
    } else {
      void authenticationStore().refreshLoginInformation();
    }
  },
  cleanUpAuthData: () => {
    const authStore = authenticationStore();
    authStore.resetStore();
    TokenService.removeAccessToken();
    TokenService.removeRefreshToken();
  },
  cleanUpAndRedirectToLogin: async (showError = false) => {
    AuthApi.cleanUpAuthData();
    if (showError) showSnackbar(i18n.t('errors.unauthorized'));
    await router.push('/login');
  },
  loginViaIdP: async (
    loginChallenge: string,
    idp?: string
  ): Promise<string> => {
    const idpParam = idp ? `&idp=${idp}` : '';
    const queryParams = `?login_challenge=${loginChallenge}${idpParam}`;

    const discoveryURL = `${import.meta.env.VITE_AUTH_URL as string}/identity/authorize/idp${queryParams}`;
    try {
      const response = await ApiService.get(
        '',
        configToAuthFrontend,
        discoveryURL
      );
      return response.data.redirectUrl as string;
    } catch (error) {
      throw new AuthenticationError(
        error.response.status,
        error.response.data.message,
        error.response.data.code,
        error.response.data.data
      );
    }
  }
};

export default AuthApi;
